import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Calendar as CalIcon, MapPin, Trash2, Link2 } from "lucide-react";

export default function CalendarPage() {
  const [events, setEvents] = useState([]);
  const [open, setOpen] = useState(false);
  const [google, setGoogle] = useState({ status: "disconnected" });
  const [form, setForm] = useState({ title: "", start: "", end: "", location: "", description: "" });

  const load = () => api.get("/events").then((r) => setEvents(r.data));
  useEffect(() => {
    load();
    api.get("/integrations").then((r) => setGoogle(r.data.google));
  }, []);

  const create = async () => {
    if (!form.title || !form.start) return;
    await api.post("/events", { ...form, start: new Date(form.start).toISOString(), end: form.end ? new Date(form.end).toISOString() : null });
    setOpen(false);
    setForm({ title: "", start: "", end: "", location: "", description: "" });
    toast.success("Event added");
    load();
  };
  const remove = async (id) => { await api.delete(`/events/${id}`); load(); };

  const syncGoogle = async () => {
    try {
      const { data } = await api.post("/integrations/google/sync-calendar");
      toast.success(`Synced ${data.synced} new event(s) from Google`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sync failed");
    }
  };

  const grouped = events.reduce((acc, e) => {
    const d = new Date(e.start).toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" });
    (acc[d] = acc[d] || []).push(e);
    return acc;
  }, {});

  return (
    <div className="space-y-6" data-testid="calendar-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="label-mono text-slate-500">Schedule</div>
          <h1 className="text-4xl font-black tracking-tighter">Calendar</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-sm bg-slate-950 hover:bg-slate-800" data-testid="new-event-button">
              <Plus size={16} className="mr-2" /> New Event
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-sm">
            <DialogHeader><DialogTitle>New Event</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <Input placeholder="Event title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="rounded-sm" data-testid="event-title-input" />
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label-mono text-slate-500">Start</label>
                  <Input type="datetime-local" value={form.start} onChange={(e) => setForm({ ...form, start: e.target.value })} className="rounded-sm" data-testid="event-start-input" />
                </div>
                <div>
                  <label className="label-mono text-slate-500">End</label>
                  <Input type="datetime-local" value={form.end} onChange={(e) => setForm({ ...form, end: e.target.value })} className="rounded-sm" />
                </div>
              </div>
              <Input placeholder="Location (optional)" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="rounded-sm" />
            </div>
            <DialogFooter>
              <Button onClick={create} className="rounded-sm bg-slate-950 hover:bg-slate-800" data-testid="save-event-button">Add Event</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {google.status !== "connected" ? (
        <Card className="rounded-sm border-dashed border-ai/40 bg-ai/5 shadow-none p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link2 size={18} className="text-ai" />
            <div className="text-sm">
              <span className="font-semibold">Two-way Google Calendar sync</span>
              <span className="text-slate-600"> — connect in Settings to sync events automatically.</span>
            </div>
          </div>
        </Card>
      ) : (
        <Card className="rounded-sm border-ai/40 bg-ai/5 shadow-none p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link2 size={18} className="text-ai" />
            <span className="text-sm font-semibold">Google Calendar connected ({google.config?.email})</span>
          </div>
          <Button size="sm" className="rounded-sm bg-ai hover:bg-blue-800" onClick={syncGoogle} data-testid="google-sync-button">Sync now</Button>
        </Card>
      )}

      <div className="space-y-6">
        {Object.keys(grouped).length === 0 && <p className="text-sm text-muted-foreground">No events scheduled.</p>}
        {Object.entries(grouped).map(([day, items]) => (
          <div key={day}>
            <div className="label-mono text-slate-500 mb-2">{day}</div>
            <div className="space-y-2">
              {items.map((e) => (
                <Card key={e.id} className="rounded-sm border-border shadow-none p-4 flex items-center gap-4 group" data-testid={`event-${e.id}`}>
                  <div className="w-10 h-10 bg-slate-950 text-white rounded-sm flex items-center justify-center"><CalIcon size={18} /></div>
                  <div className="flex-1">
                    <div className="font-semibold text-sm">{e.title}</div>
                    <div className="label-mono text-slate-400 mt-0.5 flex items-center gap-3">
                      <span>{new Date(e.start).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}</span>
                      {e.location && <span className="flex items-center gap-1"><MapPin size={11} /> {e.location}</span>}
                    </div>
                  </div>
                  <button onClick={() => remove(e.id)} className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-destructive"><Trash2 size={14} /></button>
                </Card>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
